import { Combobox, DatePicker, FormControl, FormField, FormInput, FormItem, FormLabel, FormMessage, Text } from "@sentricmusic/ui";
import { formatISO, parseISO } from "date-fns";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Setlist } from "@/api/queries";

import type { GigClaimFormValues } from "../utils/gigsClaimSchema";

import { TIME_OPTIONS } from "../utils/gigsClaimConstants";

type GigDetailsSectionProps = {
  setlists: Setlist[] | undefined;
};

function GigDetailsSection({ setlists }: GigDetailsSectionProps) {
  const { t } = useTranslation("gigs");
  const str = (s: string | null): string => s ?? "";
  const formContext = useFormContext<GigClaimFormValues>();
  return (
    <div className="flex flex-col">
      <Text as="subtitle">{t("gigs:gig-details-header", "Gig details")}</Text>
      <Text>{t("gigs:gig-details-subtitle", "Enter the performer name, date, time and setlist")}</Text>
      <div className="flex flex-col gap-2">
        <FormField
          control={formContext.control}
          name="performer"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>
                {str(t("gigs:performer-placeholder", "Performer"))}
              </FormLabel>
              <FormControl>
                <FormInput
                  placeholder={str(t("gigs:performer-placeholder", "Performer"))}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formContext.control}
          name="date"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>
                {str(t("gigs:performance-date-placeholder", "Performance Date"))}
              </FormLabel>
              <FormControl>
                <DatePicker
                  placeholder={str(t("gigs:performance-date-placeholder", "Performance Date"))}
                  value={field.value ? parseISO(field.value) : undefined}
                  onChange={date => field.onChange(date ? formatISO(date, { representation: "date" }) : undefined)}
                  disabledDays={{ after: new Date() }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formContext.control}
          name="time"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>
                {str(t("gigs:performance-time-placeholder", "Performance time"))}
              </FormLabel>
              <FormControl>
                <Combobox
                  placeholder={str(t("gigs:performance-time-placeholder", "Performance time"))}
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                  data={TIME_OPTIONS}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={formContext.control}
          name="setlist"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>
                {str(t("gigs:setlist", "Setlist"))}
              </FormLabel>
              <FormControl>
                <Combobox
                  placeholder={str(t("gigs:setlist", "Setlist"))}
                  value={field.value ?? ""}
                  onValueChange={field.onChange}
                  data={setlists?.map(s => ({ label: s.name, value: String(s.id) })) ?? []}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

export default GigDetailsSection;
